import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  MatchesPreflight,
  SsnUpdaterSummary,
} from "features/adminUsers/adminUsers";

export interface MatchesPreflightAlertProps {
  /** TaskJob.matches_preflight of an upload job; null when never checked. */
  preflight: MatchesPreflight | null;
  canEdit: boolean;
  /** A re-check request is on its way. */
  rechecking: boolean;
  onRecheck: () => void;
  onRunUpdater: () => void;
  /** Why the operator may only look; null when they may act. */
  readOnlyReason?: string | null;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function updaterLine(summary: SsnUpdaterSummary | null | undefined): string {
  if (!summary) return "";
  if (summary.status === "running") return "SSN Updater is running...";
  if (summary.status === "failed") {
    return `SSN Updater failed ${formatWhen(summary.finished_at)}: ${summary.error ?? "unknown error"}.`;
  }
  return `SSN Updater filed ${plural(summary.added ?? 0, "folder name")} under their SSNs ${formatWhen(
    summary.finished_at,
  )}.`;
}

/**
 * What the bot's matching would make of the job's employee folders, told
 * before anyone presses Run: no matches at all, some, or a check still on
 * its way. Folders named "<name> - <last 4 of SSN>" (the PDF Splitter
 * pattern) match nobody by design, and SSN Updater is offered for them.
 */
export default function MatchesPreflightAlert({
  preflight,
  canEdit,
  rechecking,
  onRecheck,
  onRunUpdater,
  readOnlyReason,
}: MatchesPreflightAlertProps) {
  // Re-check is a write for the server (POST), so a read-only operator gets
  // the reason instead of a 403.
  const recheckButton = (
    <Tooltip title={!canEdit ? (readOnlyReason ?? "Read-only access.") : ""}>
      <span>
        <Button
          size="small"
          color="inherit"
          disabled={!canEdit || rechecking || preflight?.status === "running"}
          onClick={onRecheck}
          startIcon={
            rechecking ? (
              <CircularProgress size={12} color="inherit" />
            ) : undefined
          }
        >
          {preflight ? "Re-check" : "Check matches"}
        </Button>
      </span>
    </Tooltip>
  );

  if (!preflight) {
    return (
      <Alert severity="info" sx={{ mb: 1 }} action={recheckButton}>
        The employee folders have not been checked against the SSN files yet.
      </Alert>
    );
  }
  if (preflight.status === "running") {
    return (
      <Alert
        severity="info"
        icon={<CircularProgress size={18} />}
        sx={{ mb: 1 }}
      >
        Checking{preflight.s3_folders != null ? ` ${preflight.s3_folders}` : ""}{" "}
        employee folders against the SSN files...
      </Alert>
    );
  }
  if (preflight.status === "error") {
    return (
      <Alert severity="warning" sx={{ mb: 1 }} action={recheckButton}>
        The matches check did not finish: {preflight.error ?? "unknown error"}.
      </Alert>
    );
  }

  const folders = preflight.s3_folders ?? 0;
  const estimate = preflight.updater_estimate ?? null;
  const linkable =
    (estimate?.linkable_exact ?? 0) + (estimate?.linkable_similar ?? 0);
  const fourDigit = estimate?.four_digit_tails ?? 0;
  const matched = preflight.matched_folders;
  const stopLine =
    (preflight.unsplittable ?? 0) > 0
      ? `${plural(preflight.unsplittable ?? 0, "item")} in the data folder ${
          preflight.unsplittable === 1 ? "has" : "have"
        } no " - " in the name - the bot stops on them.`
      : "";
  const updater = updaterLine(preflight.ssn_updater);

  if (preflight.algorithm !== "ssn") {
    if (fourDigit === 0 && !stopLine) return null;
    return (
      <Alert severity="info" sx={{ mb: 1 }} action={recheckButton}>
        {plural(folders, "employee folder")} in Data Storage
        {fourDigit > 0
          ? `; ${fourDigit} carry the last 4 digits of the SSN as the id (the PDF Splitter pattern), which matching by names ignores`
          : ""}
        . SSN matching is off for this job
        {preflight.reason ? ` (${preflight.reason})` : ""}.
        {stopLine ? ` ${stopLine}` : ""}
      </Alert>
    );
  }

  const splitterLine =
    fourDigit > 0
      ? `${fourDigit === folders ? "The" : `${fourDigit} of the`} folder tails are the last 4 digits of the SSN - the pattern the PDF Splitter produces - while the ${
          preflight.source_vendor
        } SSN file knows employees by ids like [${(preflight.source_id_shapes ?? []).join(", ") || "n/a"}].`
      : "";
  const updaterOffer =
    linkable > 0
      ? `SSN Updater can file ${linkable} of them under their SSNs (${estimate?.linkable_exact ?? 0} by exact name, ${
          estimate?.linkable_similar ?? 0
        } by similar name)${
          estimate?.would_match != null
            ? `; ${estimate.would_match} would then match an employee in ${preflight.destination_vendor}`
            : ""
        }.`
      : "";
  const actions = (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {linkable > 0 && (
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          disabled={!canEdit || preflight.ssn_updater?.status === "running"}
          title={readOnlyReason ?? undefined}
          onClick={onRunUpdater}
        >
          Run SSN Updater
        </Button>
      )}
      {recheckButton}
    </Stack>
  );

  if (matched === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }} action={actions}>
        <AlertTitle sx={{ fontWeight: 700 }}>
          No employee matches: none of the {plural(folders, "employee folder")}{" "}
          match the {preflight.source_vendor} SSN file.
        </AlertTitle>
        {preflight.top_reason_label ? (
          <Typography variant="body2">
            Why: {preflight.top_reason_label}.
          </Typography>
        ) : null}
        {splitterLine ? (
          <Typography variant="body2">{splitterLine}</Typography>
        ) : null}
        {updaterOffer ? (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {updaterOffer}
          </Typography>
        ) : null}
        {stopLine ? <Typography variant="body2">{stopLine}</Typography> : null}
        {updater ? <Typography variant="body2">{updater}</Typography> : null}
        <Typography variant="caption" color="text.secondary">
          Checked {formatWhen(preflight.checked_at)}. The Matches file is built
          on the next Build matches only or Run all.
        </Typography>
      </Alert>
    );
  }

  if (linkable > 0 || updater || stopLine) {
    return (
      <Alert severity="info" sx={{ mb: 1 }} action={actions}>
        {matched} of {plural(folders, "employee folder")} match the{" "}
        {preflight.source_vendor} SSN file. {updaterOffer} {stopLine} {updater}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {matched} of {plural(folders, "employee folder")} match the{" "}
        {preflight.source_vendor} SSN file (checked{" "}
        {formatWhen(preflight.checked_at)}).
      </Typography>
      {recheckButton}
    </Box>
  );
}
